import React, { useState } from 'react';
import Button from '../common/Button';
import StatusMessage from '../common/StatusMessage';
import { generateCustomerId } from '../../services/contextService';
import { parseCSV } from '../../utils/csvParser';
import { sortAndRenumberProcesses } from '../../utils/scoreCalculator';
import { defaultContext } from '../../utils/constants';

function GettingStarted({ onFilesUploaded, setActiveTab }) {
  const [status, setStatus] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      setSelectedFiles(files);
      setStatus(null);
    }
  };

  const handleProcessFiles = async () => {
    if (selectedFiles.length === 0) {
      setStatus({ type: 'error', message: 'Please select at least one file to upload' });
      return;
    }

    let contextData = null;
    let customerId = null;
    let processes = null;

    try {
      // Separate files by type
      const jsonFile = selectedFiles.find(f => f.name.endsWith('.json'));
      const csvFile = selectedFiles.find(f => f.name.endsWith('.csv'));

      // Process JSON file (Context)
      if (jsonFile) {
        const jsonText = await readFile(jsonFile);
        const loadedContext = JSON.parse(jsonText);
        contextData = { ...defaultContext, ...loadedContext };

        if (loadedContext.customer) {
          customerId = generateCustomerId(loadedContext.customer);
          // Save context to sessionStorage
          sessionStorage.setItem(`ipa-context-${customerId}`, JSON.stringify(contextData));
          sessionStorage.setItem('ipa-last-customer-id', customerId);
        }
      }

      // Process CSV file (Processes)
      if (csvFile) {
        const csvText = await readFile(csvFile);
        const parsedProcesses = parseCSV(csvText);
        processes = sortAndRenumberProcesses(parsedProcesses);

        // Save processes to localStorage
        const storageKey = customerId ? `processes_${customerId}` : 'processes_default';
        localStorage.setItem(storageKey, JSON.stringify(processes));
      }

      // Notify parent component
      if (onFilesUploaded) {
        onFilesUploaded({
          customerId,
          contextData,
          processes
        });
      }

      // Build success message
      const messages = [];
      if (jsonFile) messages.push(`Context loaded (Customer ID: ${customerId})`);
      if (csvFile) messages.push(`${processes.length} processes loaded`);

      setStatus({
        type: 'success',
        message: `✅ ${messages.join(' • ')} • All files processed and saved!`
      });

      // Clear file inputs
      setSelectedFiles([]);
      document.getElementById('file-upload').value = '';

    } catch (error) {
      setStatus({
        type: 'error',
        message: `Failed to process files: ${error.message}`
      });
    }
  };

  const readFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md mb-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Welcome to IPA Report Generator</h2>

      <div className="mb-6">
        <p className="text-gray-700 mb-3">
          This tool helps you create professional Interaction Process Automation (IPA) assessment reports.
          The workflow consists of:
        </p>

        <div className="bg-blue-50 p-4 rounded-lg mb-4">
          <h3 className="font-semibold text-blue-900 mb-2">Quick Start Guide:</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li><strong>Upload Files</strong> - Upload your customer context (JSON) and process list (CSV) below</li>
            <li><strong>Review Context</strong> - Verify customer information and customize the report introduction</li>
            <li><strong>Rate Processes</strong> - Use AI or manually rate processes across impact and complexity dimensions</li>
            <li><strong>Configure ROI</strong> - Set ROI parameters for each process (automation area, potential, interactions)</li>
            <li><strong>Generate Report</strong> - Download the complete Word document with charts and analysis</li>
          </ol>
        </div>

        <div className="bg-yellow-50 p-4 rounded-lg">
          <h3 className="font-semibold text-yellow-900 mb-2">📁 Bulk File Upload</h3>
          <p className="text-gray-700 text-sm mb-3">
            Upload your files here to automatically load and save them. You can upload one or both files:
          </p>
          <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm mb-3">
            <li><strong>JSON File:</strong> Customer context with company info, team members, and report content</li>
            <li><strong>CSV File:</strong> Process list with names and summaries for assessment</li>
          </ul>
        </div>
      </div>

      {status && (
        <StatusMessage
          type={status.type}
          message={status.message}
          onClose={() => setStatus(null)}
        />
      )}

      {/* File Upload Section */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 mb-6 hover:border-blue-400 transition-colors">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          📁 Upload Files (JSON and/or CSV)
        </label>
        <input
          id="file-upload"
          type="file"
          accept=".json,.csv"
          multiple
          onChange={handleFileSelect}
          className="block w-full text-sm text-gray-500
            file:mr-4 file:py-2 file:px-4
            file:rounded file:border-0
            file:text-sm file:font-semibold
            file:bg-blue-50 file:text-blue-700
            hover:file:bg-blue-100
            cursor-pointer"
        />
        {selectedFiles.length > 0 && (
          <div className="mt-3 space-y-1">
            {selectedFiles.map((file, index) => (
              <p key={index} className="text-xs text-green-600">
                ✓ {file.name} ({file.name.endsWith('.json') ? 'Context' : 'Processes'})
              </p>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-4">
        <Button
          variant="primary"
          onClick={handleProcessFiles}
          disabled={selectedFiles.length === 0}
          className="flex-1"
        >
          Process & Save Files
        </Button>

        <Button
          variant="secondary"
          onClick={() => setActiveTab('context')}
          className="flex-1"
        >
          Start Manual Entry
        </Button>
      </div>

      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-semibold text-gray-800 mb-2">💡 Tips:</h3>
        <ul className="list-disc list-inside space-y-1 text-gray-600 text-sm">
          <li>You can upload files individually on their respective tabs</li>
          <li>Context and processes are auto-saved when files are uploaded</li>
          <li>Use the "Customer Context" tab to download a template JSON file</li>
          <li>CSV format: First row should contain "Process ID" and "Process Name" columns</li>
        </ul>
      </div>
    </div>
  );
}

export default GettingStarted;
